import axios from "axios";
import jwt_decode from "jwt-decode";

// Create axios instance with environment-based configuration
const api = axios.create({
    baseURL: process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:5000/api',
    timeout: 30000 * 10, // 300 seconds timeout
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Token refresh function
const refreshTokenIfNeeded = async () => {
    if (!window.store) return null;
    
    try {
        const accessToken = window.store.get('account/accessToken');
        const refreshToken = window.store.get('account/refreshToken');
        const isAuthenticated = window.store.get('account/isAuthenticated');
        
        // Skip if not authenticated or no tokens
        if (!isAuthenticated || !accessToken || !refreshToken) {
            return null;
        }
        
        // Decode and check token expiration
        const decoded = jwt_decode(accessToken);
        const now = Date.now().valueOf() / 1000;
        const timeUntilExpiry = decoded.exp - now;
        
        // Refresh if token expires within 5 minutes (300 seconds)
        if (timeUntilExpiry < 300) {
            console.log('🔄 Token expires soon, refreshing proactively...');
            
            // Make refresh request directly to avoid infinite loops
            const refreshResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL || 'http://127.0.0.1:5000/api'}/refresh`, {
                headers: {
                    'Authorization': `Bearer ${refreshToken}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            
            // Update store with new token
            const newAccessToken = refreshResponse.data.token;
            window.store.set('account/accessToken', newAccessToken);
            
            console.log('✅ Token refreshed successfully');
            return newAccessToken;
        }
        
        return accessToken;
    } catch (error) {
        console.warn('⚠️ Token refresh failed:', error.message);
        // If refresh fails, clear authentication and let the request proceed
        // The 401 handler will take care of redirecting to login
        if (window.store) {
            window.store.commit('account/reset');
        }
        return null;
    }
};

// Request interceptor
api.interceptors.request.use(
    async (config) => {
        // Add timestamp for debugging
        config.metadata = { startTime: new Date() };
        
        // Skip token refresh for the refresh endpoint itself to avoid infinite loops
        if (config.url && config.url.includes('/refresh')) {
            return config;
        }
        
        // Try to refresh token proactively if needed
        let token = await refreshTokenIfNeeded();
        
        // Fallback: get token from multiple sources if refresh didn't return one
        if (!token) {
            // First try to get from localStorage/sessionStorage (fallback)
            token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
            
            // If not found, try to get from Vuex store if available
            if (!token && window.store) {
                try {
                    // Use pathify syntax
                    token = window.store.get('account/accessToken');
                } catch (e) {
                    // Store might not be available yet
                    console.warn('Store not available for token access');
                }
            }
        }
        
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Log request in development
        if (process.env.NODE_ENV === 'development') {
            console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`, {
                data: config.data,
                params: config.params,
                headers: config.headers
            });
        }
        
        return config;
    },
    (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        // Calculate request duration
        const duration = new Date() - response.config.metadata.startTime;
        
        // Log response in development
        if (process.env.NODE_ENV === 'development') {
            console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
                status: response.status,
                duration: `${duration}ms`,
                data: response.data
            });
        }
        
        return response;
    },
    (error) => {
        // Calculate request duration if available
        const duration = error.config?.metadata?.startTime 
            ? new Date() - error.config.metadata.startTime 
            : 'unknown';
        
        // Enhanced error handling
        const errorResponse = {
            message: 'An unexpected error occurred',
            status: error.response?.status || 'unknown',
            duration: duration,
            url: error.config?.url || 'unknown',
            method: error.config?.method?.toUpperCase() || 'unknown'
        };
        
        // Handle different types of errors
        if (error.response) {
            // Server responded with error status
            errorResponse.message = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
            errorResponse.data = error.response.data;
            
            // Handle specific HTTP status codes
            switch (error.response.status) {
                case 401:
                    errorResponse.message = 'Authentication required. Please log in again.';
                    // Clear invalid tokens
                    localStorage.removeItem('auth_token');
                    sessionStorage.removeItem('auth_token');
                    // Redirect to login if not already there
                    if (window.location.pathname !== '/login') {
                        window.location.href = '/login';
                    }
                    break;
                    
                case 403:
                    errorResponse.message = 'Access denied. You don\'t have permission to perform this action.';
                    break;
                    
                case 404:
                    errorResponse.message = 'The requested resource was not found.';
                    break;
                    
                case 422:
                    errorResponse.message = 'Validation error. Please check your input.';
                    break;
                    
                case 429:
                    errorResponse.message = 'Too many requests. Please try again later.';
                    break;
                    
                case 500:
                    errorResponse.message = 'Internal server error. Please try again later.';
                    break;
                    
                case 502:
                case 503:
                case 504:
                    errorResponse.message = 'Service temporarily unavailable. Please try again later.';
                    break;
            }
        } else if (error.request) {
            // Request was made but no response received
            errorResponse.message = 'No response from server. Please check your internet connection.';
        } else {
            // Something else happened
            errorResponse.message = error.message || 'An unexpected error occurred';
        }
        
        // Log error in development
        if (process.env.NODE_ENV === 'development') {
            console.error(`❌ API Error: ${errorResponse.method} ${errorResponse.url}`, {
                status: errorResponse.status,
                duration: errorResponse.duration,
                message: errorResponse.message,
                originalError: error
            });
        }
        
        // Reject with enhanced error
        return Promise.reject(errorResponse);
    }
);

// Add retry mechanism for failed requests
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const { config } = error;
        
        // Only retry for specific errors and if not already retried
        if (!config || config.__isRetryRequest || 
            !['get', 'post', 'put', 'patch'].includes(config.method) ||
            error.response?.status === 401) {
            return Promise.reject(error);
        }
        
        config.__isRetryRequest = true;
        
        // Retry once after a delay
        return new Promise(resolve => {
            setTimeout(() => {
                console.log(`🔄 Retrying request: ${config.method?.toUpperCase()} ${config.url}`);
                resolve(api(config));
            }, 1000);
        });
    }
);

// Add request/response timeout handling
api.interceptors.request.use(
    (config) => {
        // Set timeout for different request types
        if (config.method === 'get') {
            config.timeout = 15000; // 15 seconds for GET requests
        } else if (config.method === 'post' || config.method === 'put' || config.method === 'patch') {
            config.timeout = 30000 * 10; // 300 seconds for data modification requests
        }
        return config;
    }
);

// Export the enhanced axios instance
export default api;

// Export utility functions for common API operations
export const apiUtils = {
    // Helper to handle file uploads with progress
    uploadFile: (url, file, onProgress) => {
        const formData = new FormData();
        formData.append('file', file);
        
        return api.post(url, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
                if (onProgress) {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    onProgress(percentCompleted);
                }
            }
        });
    },
    
    // Helper to handle pagination
    getPaginatedData: (url, page = 1, limit = 10, params = {}) => {
        return api.get(url, {
            params: {
                page,
                limit,
                ...params
            }
        });
    },
    
    // Helper to handle search with debouncing
    search: (url, query, params = {}) => {
        return api.get(url, {
            params: {
                q: query,
                ...params
            }
        });
    }
};
